import {inject, Injectable, Injector, isDevMode, runInInjectionContext} from '@angular/core';
import {Auth}                                                           from '@angular/fire/auth';
import {doc, Firestore, getDoc, setDoc, Timestamp}                      from '@angular/fire/firestore';
import {decryptString, deriveKeyFromPassphrase, encryptString}          from './auth-crypto';
import {WebAuthnService}                                                from './web-authn.service';

@Injectable({
				providedIn: 'root'
			})
export class AuthUnlockService {


	injector = inject(Injector)
	webAuthnService = inject(WebAuthnService)


	private cryptoKey: CryptoKey | null = null;
	private unlocked = false;

	async clearKeys() {
		await this.webAuthnService.clearKeys();
		console.log('🔄 IndexedDB cleared successfully');
	}


	/** Is the encryption key currently loaded in memory? */
	isUnlocked(): boolean {
		return this.cryptoKey !== null;
	}

	/** Load the encryption key by deriving from passphrase */
	async unlockWithPassphrase(passphrase: string): Promise<void> {
		this.cryptoKey = await deriveKeyFromPassphrase(passphrase);
	}


	async unlockWithFaceId(): Promise<boolean> {
		try {
			console.log('🔐 Attempting FaceID unlock');

			const assertion = await this.webAuthnService.getAssertion();
			if (!assertion) {
				console.warn('❌ No credential returned from FaceID');
				return false;
			}

			console.log('🔐 Unlocking with credential ID:', assertion.id);

			try {
				const rawKey = await this.webAuthnService.loadWrappedKey(assertion.id);
				if (!rawKey) {
					console.warn('❌ No AES key found for credential ID:', assertion.id);
					console.log('💡 Try setting up FaceID again using setupFaceIdCredential()');
					return false;
				}

				this.cryptoKey = await this.importKey(rawKey);
				this.unlocked = true;
				console.log('✅ Unlocked via FaceID!');
				return true;
			} catch (keyError) {
				console.error('❌ Error loading or importing key:', keyError);
				return false;
			}
		} catch (error) {
			console.error('❌ FaceID unlock failed', error);
			return false;
		}
	}

	async setupFaceIdCredential(): Promise<boolean> {
		try {
			console.log('🔐 Setting up FaceID credential');

			if (!this.cryptoKey) {
				console.error('❌ No encryption key loaded. Please unlock with passphrase first.');
				return false;
			}

			try {
				const rawKey = await this.exportKey(); // AES key as ArrayBuffer
				console.log('🔑 Exported AES key for WebAuthn registration');

				// this method will internally handle WebAuthn FaceID + IndexedDB
				await this.webAuthnService.registerCredential(rawKey);
				console.log('✅ FaceID credential registered and AES key stored');
				return true;
			} catch (exportError) {
				console.error('❌ Error exporting or registering key:', exportError);
				return false;
			}
		} catch (err) {
			console.error('❌ Failed to set up FaceID credential:', err);
			return false;
		}
	}

	async tryFaceIdUnlock(): Promise<boolean> {
		try {
			console.log('🔐 Attempting FaceID unlock');

			const credential = await this.webAuthnService.getAssertion();
			if (!credential) {
				console.warn('❌ No credential returned from FaceID');
				return false;
			}

			console.log('🔐 Unlocking with credential ID:', credential.id);

			try {
				// Retrieve the AES key using the credential.id
				const rawKey = await this.webAuthnService.loadWrappedKey(credential.id);
				if (!rawKey) {
					console.warn('❌ No AES key found for credential ID:', credential.id);
					console.log('💡 Try setting up FaceID again using setupFaceIdCredential()');
					return false;
				}

				// Import the AES key back into CryptoKey format
				this.cryptoKey = await this.importKey(rawKey);

				// ✅ Successfully unlocked
				this.unlocked = true;
				console.log('✅ Successfully unlocked via FaceID');

				return true;
			} catch (keyError) {
				console.error('❌ Error loading or importing key:', keyError);
				return false;
			}
		} catch (err) {
			console.error('❌ Error during FaceID unlock:', err);
			return false;
		}
	}

	/** Clear the key from memory */
	lock(): void {
		this.cryptoKey = null;
	}

	/** Encrypt plaintext string */
	async encrypt(plainText: string): Promise<string> {
		if (isDevMode()) {
			throw new Error('Dev mode, not decrypting' +
							'')
		}
		if (!this.cryptoKey) {
			throw new Error('Encryption key not loaded.');
		}
		return encryptString(this.cryptoKey, plainText);
	}


	/** Decrypt encrypted string */
	async decrypt(encryptedText: string): Promise<string> {
		if (isDevMode()) {
			throw new Error('Dev mode, not decrypting' +
							'')
		}
		if (!this.cryptoKey) {
			throw new Error('Encryption key not loaded.');
		}
		return decryptString(this.cryptoKey, encryptedText);
	}

	async verifyPassphraseKey(): Promise<boolean> {
		return await runInInjectionContext(this.injector, async () => {
			const firestore = inject(Firestore)
			const auth = inject(Auth)
			const checkDoc = await getDoc(doc(firestore, `crypto/unlock-check`));
			if (!checkDoc.exists()) return false;

			const encryptedContent = checkDoc.data()?.['content'];
			try {
				const decrypted = await this.decrypt(encryptedContent);
				return decrypted === 'unlock-check';
			} catch {
				return false;
			}
		})
	}

	async createInitialUnlockCheck(): Promise<void> {
		return await runInInjectionContext(this.injector, async () => {
			const firestore = inject(Firestore)
			const auth = inject(Auth)
			const encrypted = await this.encrypt('unlock-check');
			await setDoc(doc(firestore, `crypto/unlock-check`), {
				content  : encrypted,
				createdAt: Timestamp.now()
			});
		})
	}

	async exportKey(): Promise<ArrayBuffer> {
		if (!this.cryptoKey) {
			throw new Error('No crypto key loaded');
		}
		return crypto.subtle.exportKey('raw', this.cryptoKey);
	}

	async importKey(raw: ArrayBuffer): Promise<CryptoKey> {
		return crypto.subtle.importKey(
			'raw',
			raw,
			{name: 'AES-GCM'},
			true,
			[
				'encrypt',
				'decrypt'
			]
		);
	}
}
